import { Component, OnInit, signal, computed, HostListener } from '@angular/core';
import { NgForm } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ClientDashboardService } from './client-dashboard.service';
import { ClientNav, Contact, Product, ProductGroup } from '../../Models/Client-Nav';
import sampleTemplate from '../../../../dashboard-structure.json';
import { RootScopeService } from '../../core/services/root-scope.service';

@Component({
  selector: 'app-client-dashboard',
  standalone: false,
  templateUrl: './client-dashboard.component.html',
  styleUrls: ['./client-dashboard.component.scss'],
})
export class ClientDashboardComponent implements OnInit {
  editProductModalOpen: boolean = false;
  hideCompletely: boolean = false;
  availableProductGroups: ProductGroup[] = [];
  editableProductGroups: ProductGroup[] = [];
  selectedProductGroupId: string = '';
  productGroupSearchTerm: string = '';
  productSearchTerm: string = '';
  productGroupDropdownOpen: boolean = false;
  productDropdownOpen: boolean = false;
  openProductStatusDropdown: string | null = null;
  creationError: string = '';
  isCreatingClient = false;
  openMultiDropdownKey: string | null = null;
  openInternalEmailDropdownKey: string | null = null;
  showAllInternalEmailDropdownKey: string | null = null;
  confirmModalOpen = false;
  confirmModalMessage = '';
  private confirmModalOkAction: (() => void) | null = null;
  private confirmModalCancelAction: (() => void) | null = null;

  // Notes (textbox) viewer/editor modal state.
  notesModalOpen = false;
  notesModalTitle = '';
  notesModalEditable = false;
  notesModalValue = '';
  private notesModalSection: any = null;
  private notesModalField: any = null;
  private notesModalRowIndex = -1;
  openSingleDropdownKey: string | null = null;
  duplicateEmailsWarning: string[] = [];
  showValidationErrors = false;

  isLoading = signal(false);
  private autoEditFirstSection = false;
  private autoEditApplied = false;

  clientName!: string;
  clientData = signal<ClientNav>({} as ClientNav);
  productGroups = signal<ProductGroup[]>([]);
  formOptions = signal<Record<string, any[]>>({});
  // Working copy for edits (shared across sections); section-level flags control UI
  editData: ClientNav = {} as ClientNav;
  // Reactive: disable while flag unresolved or false
  disableEditButton = computed(() => {
    const flag = this.rootScopeService.viewContactsFlag?.();
    return !(flag === true);
  });
  clientId!: string;
  activeTab = signal('ClientDetails');
  // Use a per-instance deep clone so edit state does not persist across navigations
  templateJson = JSON.parse(JSON.stringify(sampleTemplate));

  // Only show tabs whose product group has at least one product (Client Details always visible)
  visibleTabs = computed(() => {
    const groups = this.clientData()?.productGroups || [];
    const activeGroupIds = new Set(
      groups.filter(g => g.products && g.products.length > 0).map(g => g.groupId)
    );
    return this.templateJson.tabs.filter(
      (tab: any) => tab.tabKey === 'ClientDetails' || activeGroupIds.has(tab.tabKey)
    );
  });

  // Export functionality
  isExporting = false;

  // Computed property to check if client data exists
  hasClientData = computed(() => {
    const data = this.clientData();
    return data && Object.keys(data).length > 0 && this.clientId;
  });

  // Derive Section type from imported JSON (all sections now include isOnEditMode per JSON)
  private readonly SectionArray = this.templateJson.tabs[0]?.sections || [];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private clientDashboardService: ClientDashboardService,
    public rootScopeService: RootScopeService,
  ) {}

  ngOnInit(): void {
    // Component initialization logic
    this.isLoading.set(true);
    this.route.paramMap.subscribe((params) => {
      this.clientName = decodeURI(params.get('name') || '');
    });
    this.clientDashboardService.getAllProductGroups().subscribe({
      next: (res) => {
        this.availableProductGroups = this.normalizeProductGroupsResponse(res);
      },
    });

    this.clientDashboardService.getFormOptions().subscribe((res) => {
      this.formOptions.set(res);
    });

    this.route.queryParamMap.subscribe((queryParams) => {
      this.clientId = queryParams.get('id') || '';
      const autoEditParam = queryParams.get('autoEditFirstSection');
      this.autoEditFirstSection = autoEditParam === 'true';
      this.autoEditApplied = !this.autoEditFirstSection;
      if (this.clientId) {
        this.loadClientData(this.clientId);
        console.log(this.clientData());
      }
    });

    this.isLoading.set(false);
  }

  private loadClientData(clientId: string): void {
    this.clientDashboardService.getClient_by_id(clientId).subscribe({
      next: (response: ClientNav) => {
        console.log('Client data received:', response);

        // Attach proper logo path before storing it
        const clientWithLogo = {
          ...response
        };

        this.clientData.set(clientWithLogo as ClientNav);
        this.tryActivateFirstSectionEdit();
        this.isLoading.set(false);
      },
      error: (err) => {
        console.error('Failed to fetch client data', err);
        this.isLoading.set(false);
      },
    });
  }

  toggleProductModal(){
    if (this.editProductModalOpen) {
      this.closeEditProductModal();
      return;
    }

    this.editableProductGroups = JSON.parse(
      JSON.stringify(this.clientData().productGroups || []),
    );
    this.selectedProductGroupId = '';
    this.productGroupSearchTerm = '';
    this.productSearchTerm = '';
    this.productGroupDropdownOpen = false;
    this.productDropdownOpen = false;
    this.creationError = '';
    this.editProductModalOpen = true;
  }

  closeEditProductModal(){
    this.editProductModalOpen = false;
    this.productGroupDropdownOpen = false;
    this.productDropdownOpen = false;
    this.openProductStatusDropdown = null;
  }

  private openConfirmModal(
    message: string,
    onConfirm: () => void,
    onCancel?: () => void,
  ): void {
    this.confirmModalMessage = message;
    this.confirmModalOkAction = onConfirm;
    this.confirmModalCancelAction = onCancel || null;
    this.confirmModalOpen = true;
  }

  closeConfirmModal(confirm: boolean): void {
    const okAction = this.confirmModalOkAction;
    const cancelAction = this.confirmModalCancelAction;

    this.confirmModalOpen = false;
    this.confirmModalMessage = '';
    this.confirmModalOkAction = null;
    this.confirmModalCancelAction = null;

    if (confirm) {
      okAction?.();
      return;
    }

    cancelAction?.();
  }

  // Opens the notes modal in read-only mode for view, or editable mode when the
  // section is being edited and the field is editable.
  openNotesModal(section: any, field: any, rowIndex: number): void {
    const editable = !!section?.isOnEditMode && !!field?.editable;
    this.notesModalSection = section;
    this.notesModalField = field;
    this.notesModalRowIndex = rowIndex;
    this.notesModalEditable = editable;
    this.notesModalTitle = field?.label || 'Notes';
    this.notesModalValue = editable
      ? String(this.getEditContact(section, rowIndex, field.key) ?? '')
      : String(this.getTableRowsForSection(section)[rowIndex]?.[field.key] ?? '');
    this.notesModalOpen = true;
  }

  saveNotesModal(): void {
    if (this.notesModalEditable && this.notesModalSection && this.notesModalField) {
      this.setEditContact(
        this.notesModalSection,
        this.notesModalRowIndex,
        this.notesModalField.key,
        this.notesModalValue,
      );
    }
    this.closeNotesModal();
  }

  closeNotesModal(): void {
    this.notesModalOpen = false;
    this.notesModalTitle = '';
    this.notesModalValue = '';
    this.notesModalEditable = false;
    this.notesModalSection = null;
    this.notesModalField = null;
    this.notesModalRowIndex = -1;
  }

  UpdateProducts(){
    this.saveEditedProductGroups();
  }

  private normalizeProductGroupsResponse(
    response: ProductGroup[] | Record<string, ProductGroup>,
  ): ProductGroup[] {
    if (Array.isArray(response)) return response;
    if (!response || typeof response !== 'object') return [];
    return Object.values(response);
  }

  get filteredSelectableProductGroups(): ProductGroup[] {
    const term = this.productGroupSearchTerm.trim().toLowerCase();

    return this.availableProductGroups.filter((group) => {
      if (!this.groupHasAvailableProducts(group)) {
        return false;
      }

      if (!term) {
        return true;
      }

      return (group.groupName || '').toLowerCase().includes(term);
    });
  }

  get selectedProductGroup(): ProductGroup | null {
    if (!this.selectedProductGroupId) return null;
    return (
      this.availableProductGroups.find(
        (group) => group.groupId === this.selectedProductGroupId,
      ) || null
    );
  }

  get filteredSelectableProducts(): Product[] {
    const selectedGroup = this.selectedProductGroup;
    if (!selectedGroup) return [];

    const term = this.productSearchTerm.trim().toLowerCase();
    const currentGroup = this.editableProductGroups.find(
      (group) => group.groupId === selectedGroup.groupId,
    );
    const associatedProductIds = new Set(
      (currentGroup?.products || []).map((product) => product.productId),
    );

    return (selectedGroup.products || []).filter((product) => {
      if (associatedProductIds.has(product.productId)) {
        return false;
      }

      if (!term) {
        return true;
      }

      return (product.productName || '').toLowerCase().includes(term);
    });
  }

  get displayedProductGroups(): ProductGroup[] {
    return this.editableProductGroups.filter(
      (group) => (group.products || []).length > 0,
    );
  }

  get canSelectProducts(): boolean {
    const selectedGroup = this.selectedProductGroup;
    if (!selectedGroup) return false;
    return this.groupHasAvailableProducts(selectedGroup);
  }

  onProductGroupSearch(value: string) {
    this.productGroupSearchTerm = value;
    this.productGroupDropdownOpen = true;
  }

  onProductSearch(value: string) {
    this.productSearchTerm = value;
    this.productDropdownOpen = this.canSelectProducts;
  }

  onProductGroupInputFocus() {
    this.productGroupDropdownOpen = true;
  }

  onProductInputFocus() {
    if (!this.canSelectProducts) {
      this.productDropdownOpen = false;
      return;
    }
    this.productDropdownOpen = true;
  }

  onProductGroupInputBlur() {
    setTimeout(() => {
      this.productGroupDropdownOpen = false;
    }, 150);
  }

  onProductInputBlur() {
    setTimeout(() => {
      this.productDropdownOpen = false;
    }, 150);
  }

  selectProductGroup(group: ProductGroup) {
    this.selectedProductGroupId = group.groupId;
    this.productGroupSearchTerm = group.groupName;
    this.productSearchTerm = '';
    this.productGroupDropdownOpen = false;
    this.productDropdownOpen = this.canSelectProducts;
  }

  resetSelectedProductGroup() {
    this.selectedProductGroupId = '';
    this.productGroupSearchTerm = '';
    this.productSearchTerm = '';
    this.productDropdownOpen = false;
    this.productGroupDropdownOpen = false;
  }

  addProductToSelectedGroup(product: Product) {
    const selectedGroup = this.selectedProductGroup;
    if (!selectedGroup) return;

    let editableGroup = this.editableProductGroups.find(
      (group) => group.groupId === selectedGroup.groupId,
    );

    if (!editableGroup) {
      editableGroup = {
        groupId: selectedGroup.groupId,
        groupName: selectedGroup.groupName,
        products: [],
      };
      this.editableProductGroups.push(editableGroup);
    }

    const alreadyAssociated = editableGroup.products.some(
      (associatedProduct) => associatedProduct.productId === product.productId,
    );
    if (alreadyAssociated) return;

    editableGroup.products.push({
      productId: product.productId,
      productName: product.productName,
      productStatus: 'Active',
    });

    this.productSearchTerm = '';
    this.productDropdownOpen = this.canSelectProducts;
  }

  removeAssociatedProduct(groupId: string, productId: string) {
    const editableGroup = this.editableProductGroups.find(
      (group) => group.groupId === groupId,
    );
    if (!editableGroup) return;

    editableGroup.products = (editableGroup.products || []).filter(
      (product) => product.productId !== productId,
    );

    this.editableProductGroups = this.editableProductGroups.filter(
      (group) => (group.products || []).length > 0,
    );
  }

  get statusOptions(): string[] {
    const opts = this.getValueCaseInsensitive(this.formOptions(), 'ClientProductStatus');
    return Array.isArray(opts) ? opts : [];
  }

  toggleProductStatusDropdown(productId: string) {
    this.openProductStatusDropdown =
      this.openProductStatusDropdown === productId ? null : productId;
  }

  setProductStatus(groupId: string, productId: string, status: string) {
    const group = this.editableProductGroups.find(g => g.groupId === groupId);
    if (!group) return;
    const product = group.products.find(p => p.productId === productId);
    if (product) product.productStatus = status;
    this.openProductStatusDropdown = null;
  }

  getStatusClass(status: string | undefined): string {
    switch (status) {
      case 'Active': return 'productStatus-active';
      case 'Implementing': return 'productStatus-implementing';
      case 'Not Contracted': return 'productStatus-not-contracted';
      case 'Cancelled': return 'productStatus-cancelled';
      default: return 'productStatus-default';
    }
  }

  removeAssociatedProductGroup(groupId: string) {
    this.editableProductGroups = this.editableProductGroups.filter(
      (group) => group.groupId !== groupId,
    );

    if (this.selectedProductGroupId === groupId) {
      this.resetSelectedProductGroup();
    }
  }

  private groupHasAvailableProducts(group: ProductGroup): boolean {
    const editableGroup = this.editableProductGroups.find(
      (candidate) => candidate.groupId === group.groupId,
    );
    const associatedProductIds = new Set(
      (editableGroup?.products || []).map((product) => product.productId),
    );

    return (group.products || []).some(
      (product) => !associatedProductIds.has(product.productId),
    );
  }

  private saveEditedProductGroups() {
    if (!this.clientId) {
      this.creationError = 'Client id is missing. Unable to update products.';
      return;
    }

    if (!this.isProductGroupsDirty()) {
      this.closeEditProductModal();
      return;
    }

    this.creationError = '';
    this.isCreatingClient = true;
    const snapshot = this.clientData();
    const updatedClient = {
      ...snapshot,
      productGroups: JSON.parse(JSON.stringify(this.editableProductGroups)),
    } as ClientNav;

    this.clientData.set(updatedClient);

    this.clientDashboardService
      .updateProductGroups(this.clientId, updatedClient.productGroups || [],this.rootScopeService.globals.currentUserId!,)
      .subscribe({
        next: (updatedProductGroups) => {
          const savedProductGroups =
            updatedProductGroups ?? updatedClient.productGroups ?? [];

          this.clientData.set({
            ...snapshot,
            productGroups: this.mergeFederationFromEdited(
              savedProductGroups,
              updatedClient.productGroups || [],
            ),
          });
          this.isCreatingClient = false;
          this.closeEditProductModal();
        },
        error: (err) => {
          console.error('Failed to update product groups', err);
          this.clientData.set(snapshot);
          this.isCreatingClient = false;
          this.creationError = 'Unable to update products. Please try again.';
        },
      });
  }

  // Product-group updates don't touch federation, so keep the pre-save federation
  // if the API response omits it (prevents federation rows from vanishing).
  private mergeFederationFromEdited(
    savedGroups: ProductGroup[],
    editedGroups: ProductGroup[],
  ): ProductGroup[] {
    const fedByProductId = new Map<string, any>();
    (editedGroups || []).forEach((g: any) =>
      (g?.products || []).forEach((p: any) => {
        if (p?.productId && p?.federation) {
          fedByProductId.set(p.productId, p.federation);
        }
      }),
    );
    return (savedGroups || []).map((g: any) => ({
      ...g,
      products: (g?.products || []).map((p: any) =>
        p?.federation
          ? p
          : { ...p, federation: fedByProductId.get(p?.productId) },
      ),
    }));
  }

  canUpdateProducts(): boolean {
    return !this.isCreatingClient && this.isProductGroupsDirty();
  }

  private isProductGroupsDirty(): boolean {
    const savedGroups = this.clientData().productGroups || [];
    const editedGroups = this.editableProductGroups || [];

    if (savedGroups.length !== editedGroups.length) return true;

    for (const savedGroup of savedGroups) {
      const editedGroup = editedGroups.find(
        (group) => group.groupId === savedGroup.groupId,
      );
      if (!editedGroup) return true;

      const savedProducts = savedGroup.products || [];
      const editedProducts = editedGroup.products || [];

      if (savedProducts.length !== editedProducts.length) return true;

      const editedProductIds = new Set(
        editedProducts.map((product) => product.productId),
      );
      for (const savedProduct of savedProducts) {
        if (!editedProductIds.has(savedProduct.productId)) {
          return true;
        }
        const editedProduct = editedProducts.find(p => p.productId === savedProduct.productId);
        if (editedProduct && editedProduct.productStatus !== savedProduct.productStatus) {
          return true;
        }
      }
    }

    return false;
  }

  get sections(): any[] {
    // Using any[] to avoid union template inference issues while still leveraging JSON shape
    const tab = this.templateJson.tabs.find(
      (t: any) => t.tabKey === this.activeTab(),
    );
    return tab ? (tab.sections as any[]) : [];
  }

  // Key resolution helpers (case-insensitive, ignore non-alphanumerics)
  private normalizeKey(key: string): string {
    return (key || '').replace(/[^a-z0-9]/gi, '').toLowerCase();
  }

  private resolveObjectKeyCaseInsensitive(obj: any, key: string): string {
    if (!obj || !key) return key;
    if (Object.prototype.hasOwnProperty.call(obj, key)) return key;
    const target = this.normalizeKey(key);
    const matched = Object.keys(obj).find(
      (k) => this.normalizeKey(k) === target,
    );
    return matched ?? key;
  }

  private getValueByPathCaseInsensitive(obj: any, path: string): any {
    if (!obj || !path) return '';
    const parts = String(path)
      .split('.')
      .map((part) => part.trim())
      .filter(Boolean);
    if (parts.length === 0) return '';

    let current = obj;
    for (const part of parts) {
      if (current === null || current === undefined) return '';
      current = this.getValueCaseInsensitive(current, part);
    }

    return current;
  }

  private setValueByPathCaseInsensitive(obj: any, path: string, value: any): void {
    if (!obj || !path) return;
    const parts = String(path)
      .split('.')
      .map((part) => part.trim())
      .filter(Boolean);
    if (parts.length === 0) return;

    let current = obj;
    for (let i = 0; i < parts.length - 1; i++) {
      const resolvedKey = this.resolveObjectKeyCaseInsensitive(current, parts[i]);
      if (
        !current[resolvedKey] ||
        typeof current[resolvedKey] !== 'object' ||
        Array.isArray(current[resolvedKey])
      ) {
        current[resolvedKey] = {};
      }
      current = current[resolvedKey];
    }

    const finalKey = parts[parts.length - 1];
    const resolvedFinalKey = this.resolveObjectKeyCaseInsensitive(current, finalKey);
    if (resolvedFinalKey !== finalKey && resolvedFinalKey in current) {
      delete current[resolvedFinalKey];
    }
    current[finalKey] = value;
  }

  private getTableDataKey(section: any): string {
    return section?.dataKey || 'contacts';
  }

  private isProductGroupSourced(section: any): boolean {
    return section?.source === 'productGroup';
  }

  private readonly federationFields = [
    'uatTicket',
    'uatStatus',
    'uatDate',
    'prodTicket',
    'prodStatus',
    'prodDate',
    'details',
  ];
  readonly federationProductField = { key: 'productId' };

  // Product IDs the user revealed via "Add new row" so an empty-federation
  // product can be edited before it carries any values. Cleared on save/cancel.
  private revealedFederationProductIds = new Set<string>();

  // True when the product carries at least one non-empty federation value.
  private productHasFederation(product: any): boolean {
    const f = product?.federation;
    if (!f) return false;
    return this.federationFields.some((k) => {
      const v = f[k];
      return v !== undefined && v !== null && String(v).trim() !== '';
    });
  }

  // Only Active/Implementing products can appear in or be added to SSO Federation.
  private isFederationEligible(product: any): boolean {
    const status = String(product?.productStatus ?? '').trim().toLowerCase();
    return status === 'active' || status === 'implementing';
  }

  // Flat table row projected from a product's nested federation.
  private toFederationRow(product: any): any {
    const f = product?.federation || {};
    const row: any = {
      productId: product?.productId ?? '',
      productName: product?.productName ?? '',
    };
    this.federationFields.forEach((k) => (row[k] = f[k] ?? ''));
    return row;
  }

  // Products of the product group whose groupId matches the active tab.
  private getActiveGroupProducts(source: ClientNav | null | undefined): any[] {
    const groups = ((source as any)?.productGroups as any[]) || [];
    const group = groups.find((g: any) => g?.groupId === this.activeTab());
    return Array.isArray(group?.products) ? group.products : [];
  }

  private readTableRows(section: any, source: ClientNav): any[] {
    if (this.isProductGroupSourced(section)) {
      // Surface products that already have federation details, plus any the
      // user explicitly revealed via "Add new row".
      const includeDrafts = source === this.editData;
      return this.getActiveGroupProducts(source)
        .filter(
          (p: any) =>
            this.isFederationEligible(p) &&
            (this.productHasFederation(p) ||
              (includeDrafts &&
                this.revealedFederationProductIds.has(p?.productId))),
        )
        .map((p: any) => this.toFederationRow(p));
    }
    const rows = this.getValueByPathCaseInsensitive(
      source as any,
      this.getTableDataKey(section),
    );
    return Array.isArray(rows) ? rows : [];
  }

  private writeTableRows(
    section: any,
    rows: any[],
    target: ClientNav = this.editData,
  ): void {
    if (this.isProductGroupSourced(section)) {
      // Fold flat edited rows back into each product's nested federation by productId.
      const products = this.getActiveGroupProducts(target);
      rows.forEach((row: any) => {
        const prod = products.find((p: any) => p?.productId === row?.productId);
        if (!prod) return;
        const fed: any =
          prod.federation && typeof prod.federation === 'object'
            ? prod.federation
            : {};
        this.federationFields.forEach((k) => (fed[k] = row[k] ?? ''));
        prod.federation = fed;
      });
      return;
    }
    this.setValueByPathCaseInsensitive(
      target as any,
      this.getTableDataKey(section),
      rows,
    );
  }

  getTableRowsForSection(section: any): any[] {
    const source = section?.isOnEditMode ? this.editData : this.clientData();
    return this.readTableRows(section, source as ClientNav);
  }

  // Group products not yet shown as a federation row (Active/Implementing only).
  private getUnlistedFederationProducts(section: any): any[] {
    const products = this.getActiveGroupProducts(this.editData as ClientNav);
    const shown = new Set(
      this.readTableRows(section, this.editData as ClientNav).map(
        (r: any) => r?.productId,
      ),
    );
    return products.filter(
      (p: any) => !shown.has(p?.productId) && this.isFederationEligible(p),
    );
  }

  // Products selectable for a given row: unlisted ones plus the row's current product.
  getFederationProductOptions(
    section: any,
    rowIndex: number,
  ): { productId: string; productName: string }[] {
    if (!this.isProductGroupSourced(section)) return [];
    const products = this.getActiveGroupProducts(this.editData as ClientNav);
    const rows = this.readTableRows(section, this.editData as ClientNav);
    const currentId = rows[rowIndex]?.productId;
    const taken = new Set(
      rows
        .map((r: any) => r?.productId)
        .filter((id: any) => id && id !== currentId),
    );
    return products
      .filter(
        (p: any) =>
          !taken.has(p?.productId) &&
          (this.isFederationEligible(p) || p?.productId === currentId),
      )
      .map((p: any) => ({
        productId: p?.productId ?? '',
        productName: p?.productName ?? '',
      }));
  }

  // True when at least one product can still be added as a federation row.
  hasAvailableFederationProduct(section: any): boolean {
    if (!this.isProductGroupSourced(section)) return true;
    return this.getUnlistedFederationProducts(section).length > 0;
  }

  // The Product cell is an editable dropdown only for freshly revealed rows.
  isFederationProductCell(section: any, field: any, row: any): boolean {
    return (
      this.isProductGroupSourced(section) &&
      field?.key === 'productName' &&
      this.revealedFederationProductIds.has(row?.productId)
    );
  }

  // Reveal the first unlisted product as a new (empty) federation row.
  private addFederationRow(section: any): void {
    const next = this.getUnlistedFederationProducts(section)[0];
    if (!next?.productId) return;
    this.revealedFederationProductIds.add(next.productId);
  }

  // Repoint a revealed row to a different product, carrying over typed values.
  onFederationProductChange(
    section: any,
    rowIndex: number,
    newProductId: string,
  ): void {
    const rows = this.readTableRows(section, this.editData as ClientNav);
    const oldId = rows[rowIndex]?.productId;
    if (!newProductId || oldId === newProductId) return;
    const products = this.getActiveGroupProducts(this.editData as ClientNav);
    const oldProd = products.find((p: any) => p?.productId === oldId);
    const newProd = products.find((p: any) => p?.productId === newProductId);
    if (newProd) {
      newProd.federation =
        oldProd?.federation && typeof oldProd.federation === 'object'
          ? { ...oldProd.federation }
          : {};
    }
    if (oldProd) oldProd.federation = {};
    if (oldId) this.revealedFederationProductIds.delete(oldId);
    this.revealedFederationProductIds.add(newProductId);
  }

  selectFederationProductOption(
    section: any,
    rowIndex: number,
    productId: string,
  ): void {
    this.onFederationProductChange(section, rowIndex, productId);
    const key = this.getInternalEmailDropdownKey(
      section,
      this.federationProductField,
      rowIndex,
    );
    this.closeInternalEmailDropdownByKey(key);
  }

  // Remove a federation row: clear that product's federation and un-reveal it.
  private removeFederationRow(section: any, index: number): void {
    const rows = this.readTableRows(section, this.editData as ClientNav);
    const productId = rows[index]?.productId;
    if (!productId) return;
    const prod = this.getActiveGroupProducts(this.editData as ClientNav).find(
      (p: any) => p?.productId === productId,
    );
    if (prod) prod.federation = {};
    this.revealedFederationProductIds.delete(productId);
  }

  private clearFederationDrafts(): void {
    this.revealedFederationProductIds.clear();
  }

  // Stable form-control name so mid-list row insertion (federation) doesn't
  // shift index-based names and reset other rows' values.
  tableRowName(section: any, fieldKey: string, row: any, index: number): string {
    const key = this.isProductGroupSourced(section)
      ? (row?.productId ?? index)
      : index;
    return `${fieldKey}_${key}`;
  }

  // Stable identity for table rows so re-projected arrays don't rebuild form controls each CD cycle.
  trackTableRow(index: number, row: any): any {
    return row?.productId ?? row?.id ?? row?.contactId ?? index;
  }

  private getValueCaseInsensitive(obj: any, key: string): any {
    if (!obj || !key) return '';
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      return obj[key];
    }
    const target = this.normalizeKey(key);
    for (const k of Object.keys(obj)) {
      if (this.normalizeKey(k) === target) return obj[k];
    }
    return '';
  }

  private deleteAlternateCaseKeys(obj: any, key: string): void {
    if (!obj || !key) return;
    const target = this.normalizeKey(key);
    for (const k of Object.keys(obj)) {
      if (k !== key && this.normalizeKey(k) === target) {
        delete obj[k];
      }
    }
  }

  getClientFieldValue(key: string): string {
    const data = this.clientData();
    const val = this.getValueCaseInsensitive(data, key);
    return (val as string) ?? '';
  }

  getContactFieldValue(
    contact: Contact,
    key: string,
    defaultValue: any = '',
  ): any {
    return (contact as any)[key] ?? defaultValue ?? '';
  }

  setActiveTab(tabKey: string) {
    this.activeTab.set(tabKey);
  }

  toggleSection(section: any, collapsible: boolean) {
    if (collapsible) {
      section.collapsed = !section.collapsed;
    }
  }

  private tryActivateFirstSectionEdit() {
    if (!this.autoEditFirstSection || this.autoEditApplied) {
      return;
    }

    const currentTabKey = this.activeTab();
    const tab =
      this.templateJson.tabs.find((t: any) => t.tabKey === currentTabKey) ??
      this.templateJson.tabs[0];
    if (!tab || !Array.isArray(tab.sections) || tab.sections.length === 0) {
      return;
    }

    const section = tab.sections[0];
    if (!section) {
      return;
    }

    this.ensureEditSnapshot();
    section.collapsed = false;
    section.isOnEditMode = true;
    this.autoEditApplied = true;
  }


  // SECTION EDITING
  private ensureEditSnapshot() {
    // Initialize editData if empty (first section entering edit mode)
    if (!this.editData || Object.keys(this.editData).length === 0) {
      this.editData = JSON.parse(JSON.stringify(this.clientData()));
    }
  }

  startSectionEdit(section: any) {
    this.ensureEditSnapshot();
    section.isOnEditMode = true;
  }

  startAllSectionsEdit() {
    this.ensureEditSnapshot();
    this.sections.forEach((section: any) => {
      section.isOnEditMode = true;
      section.collapsed = false;
    });
  }

  cancelSectionEdit(section: any) {
    // Revert this section's fields from current saved clientData
    const current = this.clientData();
    if (section.type === 'table') {
      const currentRows = this.readTableRows(section, current as ClientNav);
      this.writeTableRows(section, JSON.parse(JSON.stringify(currentRows)));
    } else if (Array.isArray(section.fields)) {
      section.fields.forEach((f: any) => {
        const key = f.key;
        const cur = this.getValueCaseInsensitive(current as any, key);
        this.setEditValue(key, cur);
      });
    }
    section.isOnEditMode = false;
    if (this.isProductGroupSourced(section)) {
      this.clearFederationDrafts();
    }
    // If no sections remain in edit mode, clear editData to free memory
    if (!this.anySectionEditingInAnyTab()) {
      this.editData = {} as ClientNav;
    }
  }

  cancelAllSectionsEdit() {
    this.sections.forEach((section: any) => this.cancelSectionEdit(section));
    this.showValidationErrors = false;
  }

  private withNormalizedContactRoles(base: ClientNav): ClientNav {
    const payload = JSON.parse(JSON.stringify(base || {}));

    const mapContacts = (path: string, alwaysEmptyRole: boolean): void => {
      const contacts = this.getValueByPathCaseInsensitive(payload as any, path);
      if (!Array.isArray(contacts)) return;

      const mapped = contacts.map((entry: any) => {
        if (!entry || typeof entry !== 'object' || Array.isArray(entry)) {
          return entry;
        }

        const row = { ...entry };
        const nextRole = alwaysEmptyRole
          ? ''
          : String(
              this.getValueCaseInsensitive(row, 'contactRole') ||
                this.getValueCaseInsensitive(row, 'role') ||
                '',
            ).trim();

        this.deleteAlternateCaseKeys(row, 'contactRole');
        row.contactRole = nextRole;
        delete row.role;
        this.deleteAlternateCaseKeys(row, 'role');
        return row;
      });

      this.setValueByPathCaseInsensitive(payload as any, path, mapped);
    };

    mapContacts('clientContacts', true);
    mapContacts('internalContacts', false);

    return payload as ClientNav;
  }

  // Builds the federation write payload for the active product group.
  private buildFederationSavePayload(section: any): {
    groupId: string;
    products: { productId: string; federation: Record<string, string> | null }[];
  } {
    const groupId = this.activeTab();
    const savedProducts = this.getActiveGroupProducts(
      this.clientData() as ClientNav,
    );
    const products = this.getActiveGroupProducts(this.editData as ClientNav)
      .filter((p: any) => {
        const wasFederated = savedProducts.some(
          (saved: any) =>
            saved?.productId === p?.productId && saved?.federation != null,
        );
        return (
          wasFederated ||
          this.productHasFederation(p) ||
          this.revealedFederationProductIds.has(p?.productId)
        );
      })
      .map((p: any) => {
        const f = p?.federation || {};
        const federation: Record<string, string> = {};
        this.federationFields.forEach((k) => (federation[k] = f[k] ?? ''));
        const isRemoved =
          !this.productHasFederation(p) &&
          !this.revealedFederationProductIds.has(p?.productId);
        return {
          productId: p?.productId ?? '',
          federation: isRemoved ? null : federation,
        };
      });
    return { groupId, products };
  }

  private saveSsoFederation(section: any): void {
    const { groupId, products } = this.buildFederationSavePayload(section);

    this.isLoading.set(true);
    this.clientDashboardService
      .updateSsoFederation(
        this.clientId,
        groupId,
        products,
        this.rootScopeService.globals.currentUserId!,
      )
      .subscribe({
        next: (updatedGroups) => {
          const data = JSON.parse(
            JSON.stringify(this.clientData()),
          ) as ClientNav;
          (data as any).productGroups = updatedGroups || [];
          this.clientData.set(data);
          this.clearFederationDrafts();
          section.isOnEditMode = false;
          if (!this.anySectionEditingInAnyTab()) {
            this.editData = {} as ClientNav;
          }
          this.isLoading.set(false);
        },
        error: () => {
          this.isLoading.set(false);
        },
      });
  }

  saveSection(section: any) {
    if (!this.clientId) {
      console.warn('No client id present; cannot save.');
      return;
    }
    // Skip if nothing changed for this section (defensive; template will also disable Save)
    if (!this.isSectionDirty(section)) {
      section.isOnEditMode = false;
      if (this.isProductGroupSourced(section)) {
        this.clearFederationDrafts();
      }
      if (!this.anySectionEditingInAnyTab()) {
        this.editData = {} as ClientNav;
      }
      return;
    }
    // Product-group-sourced sections (SSO Federation) persist via a dedicated endpoint.
    if (this.isProductGroupSourced(section)) {
      this.saveSsoFederation(section);
      return;
    }
    // Build full payload by merging current saved data with edited subset
    const base = JSON.parse(JSON.stringify(this.clientData()));
    if (section.type === 'table') {
      const editedRows = this.readTableRows(section, this.editData as ClientNav);
      this.writeTableRows(
        section,
        JSON.parse(JSON.stringify(editedRows)),
        base as ClientNav,
      );
    } else if (Array.isArray(section.fields)) {
      section.fields.forEach((f: any) => {
        if (f.editable) {
          // Clean up any alternate-cased duplicates and set canonical key
          this.deleteAlternateCaseKeys(base as any, f.key);
          (base as any)[f.key] = this.getValueCaseInsensitive(
            this.editData as any,
            f.key,
          );
        }
      });
    }

    const payload = this.withNormalizedContactRoles(base as ClientNav);

    this.isLoading.set(true);
    const snapshot = JSON.parse(JSON.stringify(this.clientData()));
    this.clientData.set(payload as ClientNav);

    this.clientDashboardService
      .updateClientInfo(
        this.clientId,
        payload,
        this.rootScopeService.globals.currentUserId!,
      )
      .subscribe({
        next: (server) => {
          if (server) {
            const existingLogo = snapshot.logo;
            const serverWithLogo = {
              ...server,
              logo: existingLogo || (server as any).logo,
            } as ClientNav;
            this.clientData.set(serverWithLogo as ClientNav);
            this.showDuplicateEmailsWarning(server);
            this.syncUrlWithClientName(serverWithLogo.clientName);
          }
          section.isOnEditMode = false;
          // Update editData snapshot to reflect saved state for remaining editing sections
          if (this.anySectionEditingInAnyTab()) {
            this.editData = JSON.parse(JSON.stringify(this.clientData()));
          } else {
            this.editData = {} as ClientNav;
          }
          this.isLoading.set(false);
        },
        error: (err) => {
          console.error('Failed to save section (PUT full object)', err);
          // rollback
          this.clientData.set(snapshot);
          this.isLoading.set(false);
        },
      });
  }

  isAnySectionDirty(): boolean {
    return this.sections.some((section: any) => this.isSectionDirty(section));
  }

  // Silently update the URL when the client name changes so the breadcrumb
  // (which derives its label from router.url) stays in sync. Angular reuses
  // the component on the same route, so this does not reload the page.
  private syncUrlWithClientName(newName: string | undefined | null): void {
    if (!newName) return;
    const trimmed = String(newName).trim();
    if (!trimmed || trimmed === this.clientName) return;
    const urlName = trimmed.replace(/\s+/g, '-');
    this.router.navigate(['/ClientNavigator', urlName], {
      queryParamsHandling: 'preserve',
      replaceUrl: true,
    });
  }

    private isMissingRequiredValue(value: any): boolean {
    if (value === null || value === undefined) return true;
    if (Array.isArray(value)) return value.length === 0;
    if (typeof value === 'string') return value.trim().length === 0;
    return false;
  }

  isFieldRequired(field: any): boolean {
    if (field.required) return true;
    if (field.key === 'previousVendor') {
      const val = (this.getEditValue('previousReportingMethod') || '').toString().toLowerCase();
      return val === 'other vendor';
    }
    if (field.key === 'otherClaimSystem') {
      const val = (this.getEditValue('claimSystem') || '').toString().toLowerCase();
      return val.split(',').some((v: string) => v.trim() === 'other');
    }
    return false;
  }

  isFieldInvalid(field: any): boolean {
    if (!this.showValidationErrors) return false;
    if (!this.isFieldRequired(field)) return false;
    const value = this.getValueCaseInsensitive(this.editData as any, field.key);
    return this.isMissingRequiredValue(value);
  }

  isTableFieldInvalid(section: any, field: any, rowIndex: number): boolean {
    if (!this.showValidationErrors) return false;
    if (!field?.required) return false;
    const raw = this.getEditContact(section, rowIndex, field.key);
    const value = typeof raw === 'string' ? raw : this.getOptionEmail(raw);
    return this.isMissingRequiredValue(value);
  }

  isRequiredFieldsMissing(): boolean {
    if (!this.anySectionEditing()) return false;

    for (const section of this.sections) {
      if (!section?.isOnEditMode) continue;

      if (section.type === 'table') {
        const requiredTableFields = (section.fields || []).filter(
          (field: any) => field?.required,
        );
        const emailTableFields = (section.fields || []).filter((field: any) =>
          this.isTableEmailField(field),
        );
        const phoneTableFields = (section.fields || []).filter((field: any) =>
          this.isClientContactsPhoneField(section, field),
        );
        if (
          requiredTableFields.length === 0 &&
          emailTableFields.length === 0 &&
          phoneTableFields.length === 0
        ) {
          continue;
        }

        const tableRows = this.getTableRowsForSection(section);
        for (const contact of tableRows as any[]) {
          for (const field of requiredTableFields) {
            const value = this.getContactFieldValue(contact as any, field.key);
            if (this.isMissingRequiredValue(value)) return true;
          }

          for (const field of emailTableFields) {
            const raw = this.getContactFieldValue(contact as any, field.key, '');
            const email = typeof raw === 'string' ? raw : this.getOptionEmail(raw);
            const trimmed = String(email || '').trim();
            if (!trimmed) continue;

            if (!this.isValidEmail(trimmed)) {
              return true;
            }
          }

          for (const field of phoneTableFields) {
            const raw = this.getContactFieldValue(contact as any, field.key, '');
            const trimmed = String(raw || '').trim();
            if (!trimmed) continue;

            if (!this.isValidPhoneNumber(trimmed)) {
              return true;
            }
          }
        }

        continue;
      }

      for (const field of section.fields || []) {
        if (!this.isFieldRequired(field)) continue;

        const value = this.getValueCaseInsensitive(this.editData as any, field.key);
        if (this.isMissingRequiredValue(value)) return true;
      }
    }

    return false;
  }

  saveAllSections() {
    if (!this.clientId) {
      console.warn('No client id present; cannot save.');
      return;
    }

    if (this.isRequiredFieldsMissing()) {
      this.showValidationErrors = true;
      return;
    }
    this.showValidationErrors = false;

    if (!this.isAnySectionDirty()) {
      this.sections.forEach((section: any) => {
        section.isOnEditMode = false;
      });
      if (!this.anySectionEditingInAnyTab()) {
        this.editData = {} as ClientNav;
      }
      return;
    }

    const base = JSON.parse(JSON.stringify(this.clientData()));

    // SSO Federation lives on client_products items and must be persisted via
    // its own endpoint; it is NOT part of the full-client PUT.
    const federationSections = this.sections.filter(
      (s: any) =>
        s.isOnEditMode &&
        this.isProductGroupSourced(s) &&
        this.isSectionDirty(s),
    );
    const hasClientLevelChanges = this.sections.some(
      (s: any) =>
        s.isOnEditMode &&
        !this.isProductGroupSourced(s) &&
        this.isSectionDirty(s),
    );

    this.sections.forEach((section: any) => {
      if (!section.isOnEditMode) return;

      if (this.isProductGroupSourced(section)) return; // handled via dedicated endpoint

      if (section.type === 'table') {
        const editedRows = this.readTableRows(section, this.editData as ClientNav);
        this.writeTableRows(
          section,
          JSON.parse(JSON.stringify(editedRows)),
          base as ClientNav,
        );
        return;
      }

      if (Array.isArray(section.fields)) {
        section.fields.forEach((field: any) => {
          if (!field.editable) return;
          this.deleteAlternateCaseKeys(base as any, field.key);
          (base as any)[field.key] = this.getValueCaseInsensitive(
            this.editData as any,
            field.key,
          );
        });
      }
    });

    const payload = this.withNormalizedContactRoles(base as ClientNav);

    this.isLoading.set(true);
    const snapshot = JSON.parse(JSON.stringify(this.clientData()));

    const finalize = () => {
      this.sections.forEach((section: any) => {
        section.isOnEditMode = false;
      });
      if (this.anySectionEditingInAnyTab()) {
        this.editData = JSON.parse(JSON.stringify(this.clientData()));
      } else {
        this.editData = {} as ClientNav;
      }
      this.clearFederationDrafts();
      this.isLoading.set(false);
    };

    const saveFederationThenFinalize = () => {
      if (federationSections.length === 0) {
        finalize();
        return;
      }
      let remaining = federationSections.length;
      federationSections.forEach((section: any) => {
        const { groupId, products } = this.buildFederationSavePayload(section);
        this.clientDashboardService
          .updateSsoFederation(
            this.clientId,
            groupId,
            products,
            this.rootScopeService.globals.currentUserId!,
          )
          .subscribe({
            next: (updatedGroups) => {
              const data = JSON.parse(
                JSON.stringify(this.clientData()),
              ) as ClientNav;
              (data as any).productGroups = updatedGroups || [];
              this.clientData.set(data);
              if (--remaining === 0) finalize();
            },
            error: (err) => {
              console.error('Failed to save SSO federation', err);
              if (--remaining === 0) this.isLoading.set(false);
            },
          });
      });
    };

    // If only federation changed, skip the full-client PUT entirely.
    if (!hasClientLevelChanges) {
      saveFederationThenFinalize();
      return;
    }

    this.clientData.set(payload as ClientNav);

    this.clientDashboardService
      .updateClientInfo(
        this.clientId,
        payload,
        this.rootScopeService.globals.currentUserId!,
      )
      .subscribe({
        next: (server) => {
          if (server) {
            const existingLogo = snapshot.logo;
            const serverWithLogo = {
              ...server,
              logo: existingLogo || (server as any).logo,
            } as ClientNav;
            this.clientData.set(serverWithLogo as ClientNav);
            this.showDuplicateEmailsWarning(server);
            this.syncUrlWithClientName(serverWithLogo.clientName);
          }

          saveFederationThenFinalize();
        },
        error: (err) => {
          console.error('Failed to save all sections (PUT full object)', err);
          this.clientData.set(snapshot);
          this.isLoading.set(false);
        },
      });
  }

  anySectionEditing(): boolean {
    const tab = this.templateJson.tabs.find(
      (t: any) => t.tabKey === this.activeTab(),
    );
    if (!tab) return false;
    return tab.sections?.some((s: any) => s.isOnEditMode);
  }

  private anySectionEditingInAnyTab(): boolean {
    return this.templateJson.tabs.some(
      (t: any) => t.sections?.some((s: any) => s.isOnEditMode)
    );
  }

  private showDuplicateEmailsWarning(response: any): void {
    const dupes: string[] = response?.duplicateEmails;
    if (Array.isArray(dupes) && dupes.length > 0) {
      this.duplicateEmailsWarning = dupes;
    }
  }

  dismissDuplicateEmailsWarning(): void {
    this.duplicateEmailsWarning = [];
  }

  // Helpers to safely access editData by dynamic keys (avoids template indexing type errors)
  getEditValue(key: string): any {
    return this.getValueCaseInsensitive(this.editData as any, key);
  }

  setEditValue(key: string, value: any): void {
    // keep only the canonical key in editData
    this.deleteAlternateCaseKeys(this.editData as any, key);
    (this.editData as any)[key] = value;
  }

  getEditContact(section: any, index: number, key: string): any {
    const rows = this.getTableRowsForSection(section);
    return ((rows as any)[index] || {})[key];
  }

  setEditContact(section: any, index: number, key: string, value: any): void {
    const rows = this.readTableRows(section, this.editData as ClientNav);
    if (!rows[index]) rows[index] = {};
    rows[index][key] = value;
    this.writeTableRows(section, rows);
  }

  private getSavedTableRows(section: any): any[] {
    return this.readTableRows(section, this.clientData() as ClientNav);
  }

  private autofillInternalContactIfNeeded(
    section: any,
    rowIndex: number,
    email: string,
  ): void {
    if (!this.isInternalContactsSection(section) || !this.normalizeEmail(email)) {
      return;
    }

    this.autofillInternalContactFields(section, rowIndex, email);
  }

  private applyEmailChange(
    section: any,
    rowIndex: number,
    fieldKey: string,
    email: string,
  ): void {
    this.setEditContact(section, rowIndex, fieldKey, email);
    this.autofillInternalContactIfNeeded(section, rowIndex, email);
  }

  onTableTextInputChange(
    section: any,
    field: any,
    index: number,
    value: any,
  ): void {
    const fieldKey = field?.key;
    if (!fieldKey) return;

    this.setEditContact(section, index, fieldKey, value);
  }

  onTableSelectChange(
    section: any,
    field: any,
    index: number,
    value: any,
  ): void {
    const fieldKey = field?.key;
    const nextValue = value ?? '';

    if (!fieldKey) return;

    const isEmailField = this.normalizeKey(fieldKey) === this.normalizeKey('emailAddress');

    if (isEmailField) {
      const selectedEmail = this.getOptionEmail(nextValue);
      if (selectedEmail && !this.isValidEmail(selectedEmail)) {
        this.setEditContact(section, index, fieldKey, selectedEmail);
        return;
      }
    }

    if (!isEmailField || !this.supportsCreateNewOnEmailChange(section)) {
      this.setEditContact(section, index, fieldKey, nextValue);
      return;
    }

    const selectedEmail = this.getOptionEmail(nextValue);
    const handledByCreateNew = this.handleEmailChangeAsNewContact(
      section,
      index,
      selectedEmail,
      () => this.applyEmailChange(section, index, fieldKey, selectedEmail),
    );
    if (handledByCreateNew) return;

    this.applyEmailChange(section, index, fieldKey, selectedEmail);
  }

  onTableInputBlur(section: any, field: any, index: number): void {
    const fieldKey = field?.key;
    if (!fieldKey) return;

    const isEmailField =
      this.normalizeKey(fieldKey) === this.normalizeKey('emailAddress');
    if (!isEmailField) return;

    const currentValue = String(this.getEditContact(section, index, fieldKey) || '').trim();
    if (!currentValue) return;

    if (!this.isValidEmail(currentValue)) {
      return;
    }

    if (!this.supportsCreateNewOnEmailChange(section)) return;

    const handledByCreateNew = this.handleEmailChangeAsNewContact(
      section,
      index,
      currentValue,
      () => this.applyEmailChange(section, index, fieldKey, currentValue),
    );
    if (handledByCreateNew) return;

    this.autofillInternalContactIfNeeded(section, index, currentValue);
  }

  private isInternalContactsSection(section: any): boolean {
    return (
      this.normalizeKey(this.getTableDataKey(section)) ===
      this.normalizeKey('internalContacts')
    );
  }

  private isClientContactsSection(section: any): boolean {
    return (
      this.normalizeKey(this.getTableDataKey(section)) ===
      this.normalizeKey('clientContacts')
    );
  }

  private isClientContactsPhoneField(section: any, field: any): boolean {
    return (
      this.isClientContactsSection(section) &&
      this.isTablePhoneField(field)
    );
  }

  private supportsCreateNewOnEmailChange(section: any): boolean {
    return (
      this.isInternalContactsSection(section) ||
      this.isClientContactsSection(section)
    );
  }

  private handleEmailChangeAsNewContact(
    section: any,
    index: number,
    selectedEmail: string,
    onConfirm: () => void,
  ): boolean {
    const normalizedSelectedEmail = this.normalizeEmail(selectedEmail);
    const currentRow = (this.getTableRowsForSection(section)[index] || {}) as any;
    const currentRowId = String(
      this.getValueCaseInsensitive(currentRow, 'id') ||
      this.getValueCaseInsensitive(currentRow, 'contactId') ||
      '',
    ).trim();

    if (!currentRowId) {
      return false;
    }

    const savedRow = this.getSavedTableRows(section).find((row: any) => {
      const savedRowId = String(
        this.getValueCaseInsensitive(row, 'id') ||
        this.getValueCaseInsensitive(row, 'contactId') ||
        '',
      ).trim();
      return savedRowId === currentRowId;
    });

    if (!savedRow) {
      return false;
    }

    const savedEmailRaw = String(
      this.getValueCaseInsensitive(savedRow, 'emailAddress') ||
        '',
    ).trim();
    const savedEmail = this.normalizeEmail(savedEmailRaw);
    const hasExistingEmail = savedEmail.length > 0;

    const changingExistingEmail =
      hasExistingEmail &&
      normalizedSelectedEmail.length > 0 &&
      normalizedSelectedEmail !== savedEmail;

    if (!changingExistingEmail) {
      return false;
    }

    this.openConfirmModal(
      'Changing this email will create a new contact and replaces the existing one. Are you sure, you want to continue ?',
      onConfirm,
      () => {
        this.setEditContact(section, index, 'emailAddress', savedEmailRaw || '');
      },
    );
    return true;
  }

  private getInternalContactDirectory(): any[] {
    const optionsSource = this.formOptions();
    const fromOptions = this.getValueCaseInsensitive(optionsSource, 'InternalContact');
    const fromClientInternal = this.getValueByPathCaseInsensitive(
      this.clientData() as any,
      'InternalContacts',
    );
    const fromClientContacts = this.getValueByPathCaseInsensitive(
      this.clientData() as any,
      'contacts',
    );

    const directory = [
      ...(Array.isArray(fromOptions) ? fromOptions : []),
      ...(Array.isArray(fromClientInternal) ? fromClientInternal : []),
      ...(Array.isArray(fromClientContacts) ? fromClientContacts : []),
    ].filter((item: any) => item && typeof item === 'object');

    if (directory.length > 0) {
      return directory;
    }

    return [];
  }

  private findInternalContactByEmail(email: string): any | null {
    const target = String(email || '').trim().toLowerCase();
    if (!target) return null;

    const directory = this.getInternalContactDirectory();
    for (const item of directory) {
      if (!item || typeof item !== 'object') continue;
      const candidate = this.normalizeEmail(this.getOptionEmail(item));
      if (candidate === target) return item;
    }

    return null;
  }

  private autofillInternalContactFields(
    section: any,
    rowIndex: number,
    selectedEmail: string,
  ): void {
    const match = this.findInternalContactByEmail(selectedEmail);
    if (!match) {
      // No match in directory — clear id so backend treats it as a new contact
      this.setEditContact(section, rowIndex, 'id', '');
      return;
    }

    const contactId =
      this.getValueCaseInsensitive(match, 'id') || '';
    const firstName =
      this.getValueCaseInsensitive(match, 'firstName') ||
      '';
    const lastName =
      this.getValueCaseInsensitive(match, 'lastName') ||
      '';
    const note =
      this.getValueCaseInsensitive(match, 'notes') ||
      '';
    const role =
      this.getValueCaseInsensitive(match, 'contactRole') ||
      this.getValueCaseInsensitive(match, 'role') ||
      '';

    this.setEditContact(section, rowIndex, 'id', contactId);
    if (firstName) this.setEditContact(section, rowIndex, 'firstName', firstName);
    if (lastName) this.setEditContact(section, rowIndex, 'lastName', lastName);
    if (note) this.setEditContact(section, rowIndex, 'notes', note);
    if (role) this.setEditContact(section, rowIndex, 'contactRole', role);
  }

  getFieldOptions(field: any): any[] {
    if (!field) return [];

    const sourceKey = field.optionsKey || field.key;
    const sourceValue = this.getValueCaseInsensitive(this.formOptions(), sourceKey);
    if (Array.isArray(sourceValue)) {
      return sourceValue;
    }

    return Array.isArray(field.options) ? field.options : [];
  }

  getOptionEmail(option: any): string {
    if (option === null || option === undefined) return '';
    if (typeof option === 'string') return option;

    const email =
      this.getValueCaseInsensitive(option, 'email') ||
      this.getValueCaseInsensitive(option, 'Email') ||
      this.getValueCaseInsensitive(option, 'emailAddress') ||
      this.getValueCaseInsensitive(option, 'EmailAddress');
    return String(email || '').trim();
  }

  private isTableEmailField(field: any): boolean {
    return (
      this.normalizeKey(field?.key) === this.normalizeKey('emailAddress')
    );
  }

  private isTablePhoneField(field: any): boolean {
    return (
      this.normalizeKey(field?.key) === this.normalizeKey('phoneNumber')
    );
  }

  private isValidEmail(value: string): boolean {
    const email = String(value || '').trim();
    if (!email) return false;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

 private isValidPhoneNumber(value: string): boolean {
  const raw = String(value || '').trim();
  if (!raw) return true;
  return !/[A-Za-z]/.test(raw);
}

  isTableEmailInvalid(section: any, field: any, rowIndex: number): boolean {
    if (!section?.isOnEditMode) return false;
    if (!this.isTableEmailField(field)) return false;

    const raw = this.getEditContact(section, rowIndex, field.key);
    const email = typeof raw === 'string' ? raw : this.getOptionEmail(raw);
    const trimmed = String(email || '').trim();

    if (!trimmed) return false;
    return !this.isValidEmail(trimmed);
  }

  isTablePhoneInvalid(section: any, field: any, rowIndex: number): boolean {
    if (!section?.isOnEditMode) return false;
    if (!this.isClientContactsPhoneField(section, field)) return false;

    const raw = this.getEditContact(section, rowIndex, field.key);
    const trimmed = String(raw ?? '').trim();

    if (!trimmed) return false;
    return !this.isValidPhoneNumber(trimmed);
  }

  private normalizeEmail(value: any): string {
    return String(value ?? '').trim().toLowerCase();
  }

  isInternalContactsEmailField(section: any, field: any): boolean {
    return (
      this.isInternalContactsSection(section) &&
      this.normalizeKey(field?.key) === this.normalizeKey('emailAddress')
    );
  }

  private getInternalEmailDropdownKey(
    section: any,
    field: any,
    rowIndex: number,
  ): string {
    const sectionKey = this.normalizeKey(this.getTableDataKey(section) || 'table');
    const fieldKey = this.normalizeKey(field?.key || 'field');
    return `internal_email_opts_${sectionKey}_${fieldKey}_${rowIndex}`;
  }

  isInternalEmailDropdownOpen(section: any, field: any, rowIndex: number): boolean {
    return (
      this.openInternalEmailDropdownKey ===
      this.getInternalEmailDropdownKey(section, field, rowIndex)
    );
  }

  private openInternalEmailDropdown(
    section: any,
    field: any,
    rowIndex: number,
    showAll: boolean = false,
  ): void {
    const key = this.getInternalEmailDropdownKey(section, field, rowIndex);
    this.openInternalEmailDropdownKey = key;
    this.showAllInternalEmailDropdownKey = showAll ? key : null;
  }

  private closeInternalEmailDropdownByKey(key: string): void {
    if (this.openInternalEmailDropdownKey === key) {
      this.openInternalEmailDropdownKey = null;
    }
    if (this.showAllInternalEmailDropdownKey === key) {
      this.showAllInternalEmailDropdownKey = null;
    }
  }

  onInternalEmailInputChange(
    section: any,
    field: any,
    rowIndex: number,
    value: any,
  ): void {
    if (!field?.key) return;
    this.setEditContact(section, rowIndex, field.key, value);
    this.openInternalEmailDropdown(section, field, rowIndex);
  }

  onInternalEmailInputFocus(section: any, field: any, rowIndex: number): void {
    this.openInternalEmailDropdown(section, field, rowIndex);
  }

  onInternalEmailInputBlur(section: any, field: any, rowIndex: number): void {
    this.onTableInputBlur(section, field, rowIndex);

    const key = this.getInternalEmailDropdownKey(section, field, rowIndex);
    setTimeout(() => {
      this.closeInternalEmailDropdownByKey(key);
    }, 120);
  }

  toggleInternalEmailDropdownAll(
    section: any,
    field: any,
    rowIndex: number,
  ): void {
    const key = this.getInternalEmailDropdownKey(section, field, rowIndex);
    const isOpenWithAll =
      this.openInternalEmailDropdownKey === key &&
      this.showAllInternalEmailDropdownKey === key;

    if (isOpenWithAll) {
      this.closeInternalEmailDropdownByKey(key);
      return;
    }

    this.openInternalEmailDropdown(section, field, rowIndex, true);
  }

  selectInternalEmailOption(
    section: any,
    field: any,
    rowIndex: number,
    value: any,
  ): void {
    this.onTableSelectChange(section, field, rowIndex, value);

    const key = this.getInternalEmailDropdownKey(section, field, rowIndex);
    this.closeInternalEmailDropdownByKey(key);
  }

  getInternalEmailDropdownOptions(section: any, field: any, rowIndex: number): any[] {
    const options = this.getTableFieldOptions(section, field, rowIndex);
    const key = this.getInternalEmailDropdownKey(section, field, rowIndex);

    if (this.showAllInternalEmailDropdownKey === key) {
      return options;
    }

    const typed = this.normalizeEmail(
      this.getEditContact(section, rowIndex, field?.key),
    );
    if (!typed) {
      return options;
    }

    return options.filter((opt: any) =>
      this.normalizeEmail(this.getOptionEmail(opt)).includes(typed),
    );
  }

  getTableFieldOptions(section: any, field: any, rowIndex: number): any[] {
    const allOptions = this.getFieldOptions(field);
    if (!this.isInternalContactsEmailField(section, field)) {
      return allOptions;
    }

    const rows = this.getTableRowsForSection(section);
    const usedEmails = new Set(
      rows
        .map((row: any, index: number) =>
          index === rowIndex ? '' : this.getValueCaseInsensitive(row, field.key),
        )
        .map((email: any) => this.normalizeEmail(email))
        .filter(Boolean),
    );

    const currentValue = this.normalizeEmail(
      this.getEditContact(section, rowIndex, field.key),
    );

    const filteredOptions: any[] = [];
    const seenEmails = new Set<string>();

    for (const option of allOptions) {
      const normalized = this.normalizeEmail(this.getOptionEmail(option));
      if (!normalized) continue;

      const allowed = !usedEmails.has(normalized) || normalized === currentValue;
      if (!allowed) continue;

      if (seenEmails.has(normalized)) continue;
      seenEmails.add(normalized);
      filteredOptions.push(option);
    }

    if (currentValue && !seenEmails.has(currentValue)) {
      filteredOptions.unshift(this.getEditContact(section, rowIndex, field.key));
    }

    return filteredOptions;
  }

  private hasKeyCaseInsensitive(obj: any, key: string): boolean {
    if (!obj || !key) return false;
    if (Object.prototype.hasOwnProperty.call(obj, key)) return true;
    const target = this.normalizeKey(key);
    return Object.keys(obj).some((k) => this.normalizeKey(k) === target);
  }

  private normalizeMultiValue(value: any): string {
    return String(value ?? '').trim().toLowerCase();
  }

  private getMultiSelectedValues(key: string): string[] {
    // Important: if editData has the key (even as empty string), use it.
    // Falling back on falsy values causes removed selections to reappear.
    const useEditData = this.hasKeyCaseInsensitive(this.editData as any, key);
    const raw = useEditData
      ? this.getValueCaseInsensitive(this.editData as any, key)
      : this.getValueCaseInsensitive(this.clientData() as any, key);

    if (!raw) return [];

    if (Array.isArray(raw)) {
      return raw.map((value) => String(value).trim()).filter(Boolean);
    }

    return String(raw)
      .split(/[;,]/)
      .map((value) => value.trim())
      .filter(Boolean);
  }

  getMultiDisplayValue(key: string): string {
    return this.getMultiSelectedValues(key).join(', ');
  }

  isOptionSelected(key: string, option: string): boolean {
    const normalizedOption = this.normalizeMultiValue(option);
    return this.getMultiSelectedValues(key).some(
      (value) => this.normalizeMultiValue(value) === normalizedOption,
    );
  }

  toggleMultiDropdown(key: string): void {
    this.openMultiDropdownKey =
      this.openMultiDropdownKey === key ? null : key;
  }

  isMultiDropdownOpen(key: string): boolean {
    return this.openMultiDropdownKey === key;
  }

  toggleSingleDropdown(key: string): void {
    this.openSingleDropdownKey =
      this.openSingleDropdownKey === key ? null : key;
  }

  isSingleDropdownOpen(key: string): boolean {
    return this.openSingleDropdownKey === key;
  }

  private closeSelectionDropdowns(): void {
    this.openSingleDropdownKey = null;
    this.openMultiDropdownKey = null;
    this.openInternalEmailDropdownKey = null;
    this.showAllInternalEmailDropdownKey = null;
  }

  @HostListener('document:mousedown', ['$event'])
  onDocumentMouseDown(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    if (target?.closest('[data-dropdown-scope="true"]')) {
      return;
    }

    this.closeSelectionDropdowns();

    // Close product modal dropdowns when clicking outside their inputs
    if (this.productGroupDropdownOpen && !target?.closest('#productGroupInput')) {
      this.productGroupDropdownOpen = false;
    }
    if (this.productDropdownOpen && !target?.closest('#productInput')) {
      this.productDropdownOpen = false;
    }
    if (this.openProductStatusDropdown && !target?.closest('[data-status-dropdown]')) {
      this.openProductStatusDropdown = null;
    }
  }

  hasOpenDropdownInSection(section: any): boolean {
    if (!section?.fields) return false;

    const hasStandardDropdownOpen = section.fields.some((f: any) =>
      f.key === this.openSingleDropdownKey || f.key === this.openMultiDropdownKey,
    );
    if (hasStandardDropdownOpen) return true;

    const internalDropdownKey =
      this.openInternalEmailDropdownKey || this.showAllInternalEmailDropdownKey;
    if (!internalDropdownKey) return false;

    const sectionKey = this.normalizeKey(this.getTableDataKey(section) || 'table');
    return internalDropdownKey.startsWith(`internal_email_opts_${sectionKey}_`);
  }

  selectSingleOption(key: string, value: string): void {
    this.setEditValue(key, value);
    this.openSingleDropdownKey = null;
  }

  /** Convert a stored date string (yyyy-MM-dd or ISO) to a Date object for kendo-datepicker */
  getEditDateValue(key: string): Date | null {
    const val = this.getEditValue(key);
    if (!val) return null;
    // Parse plain yyyy-MM-dd as a LOCAL date to avoid UTC shifting it back a day
    // for users in negative UTC offsets (e.g. US timezones).
    const dateOnlyMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(val);
    if (dateOnlyMatch) {
      const [, y, m, d] = dateOnlyMatch;
      return new Date(Number(y), Number(m) - 1, Number(d));
    }    const d = new Date(val);
    return isNaN(d.getTime()) ? null : d;
  }

  /** Convert a Date object back to yyyy-MM-dd string for storage */
  onDateChange(key: string, value: Date | null): void {
    if (!value) {
      this.setEditValue(key, '');
      return;
    }
    const yyyy = value.getFullYear();
    const mm = String(value.getMonth() + 1).padStart(2, '0');
    const dd = String(value.getDate()).padStart(2, '0');
    this.setEditValue(key, `${yyyy}-${mm}-${dd}`);
  }

  /** Row-aware date getter for table cells (mirrors getEditDateValue). */
  getEditContactDateValue(section: any, index: number, key: string): Date | null {
    const val = this.getEditContact(section, index, key);
    if (!val) return null;
    const dateOnlyMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(val);
    if (dateOnlyMatch) {
      const [, y, m, d] = dateOnlyMatch;
      return new Date(Number(y), Number(m) - 1, Number(d));
    }
    const d = new Date(val);
    return isNaN(d.getTime()) ? null : d;
  }

  /** Row-aware date setter for table cells (mirrors onDateChange). */
  onContactDateChange(section: any, index: number, key: string, value: Date | null): void {
    if (!value) {
      this.setEditContact(section, index, key, '');
      return;
    }
    const yyyy = value.getFullYear();
    const mm = String(value.getMonth() + 1).padStart(2, '0');
    const dd = String(value.getDate()).padStart(2, '0');
    this.setEditContact(section, index, key, `${yyyy}-${mm}-${dd}`);
  }

  onMultiCheckboxChange(key: string, option: string, checked: boolean): void {
    const selectedValues = this.getMultiSelectedValues(key);
    const normalizedOption = this.normalizeMultiValue(option);
    const optionIndex = selectedValues.findIndex(
      (value) => this.normalizeMultiValue(value) === normalizedOption,
    );

    if (checked && optionIndex === -1) {
      selectedValues.push(option);
    } else if (!checked && optionIndex !== -1) {
      selectedValues.splice(optionIndex, 1);
    }

    this.setEditValue(key, selectedValues.join(','));
  }

  private createEmptyContact(): Contact {
    return {
      id: '',
      firstName: '',
      lastName: '',
      emailAddress: '',
      contactPhone: { phoneType: '', phoneNumber: '' },
      title: '',
      timeZone: '',
      contactType: '',
      note: '',
      middleName: '',
      state: '',
      country: '',
      zip: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
    };
  }

  addContact(section: any) {
    if (!section?.isOnEditMode || section?.type !== 'table') return;

    if (this.isProductGroupSourced(section)) {
      this.addFederationRow(section);
      return;
    }

    const rows = [...this.readTableRows(section, this.editData as ClientNav)];
    rows.push(this.createEmptyContact());
    this.writeTableRows(section, rows);
  }

  confirmRemoveContact(section: any, index: number): void {
    if (!section?.isOnEditMode || section?.type !== 'table') return;

    const message = this.isProductGroupSourced(section)
      ? 'Are you sure you want to remove this record?'
      : 'Are you sure you want to remove the contact for this client?';

    this.openConfirmModal(message, () => {
      this.removeContact(section, index);
    });
  }

  removeContact(section: any, index: number): void {
    if (!section?.isOnEditMode || section?.type !== 'table') return;

    if (this.isProductGroupSourced(section)) {
      this.removeFederationRow(section, index);
      return;
    }

    const rows = [...this.readTableRows(section, this.editData as ClientNav)];

    if (index < 0 || index >= rows.length) return;

    rows.splice(index, 1);
    this.writeTableRows(section, rows);
  }

  // Template-driven form submit handler - validates and delegates to existing saveSection
  onSubmitSection(section: any, form: NgForm) {
    if (this.isRequiredFieldsMissing()) {
      this.showValidationErrors = true;
      if (form.invalid) {
        Object.values(form.controls).forEach((ctrl) => ctrl.markAsTouched());
      }
      return;
    }

    if (form.invalid) {
      Object.values(form.controls).forEach((ctrl) => ctrl.markAsTouched());
      return;
    }
    this.showValidationErrors = false;
    this.saveSection(section);
  }

  // Dirty check helpers
  /** Returns true if the current edit snapshot differs from saved clientData for the provided section */
  isSectionDirty(section: any): boolean {
    if (!section?.isOnEditMode) return false;
    const current = this.clientData();
    if (section.type === 'table') {
      const originalContacts = this.readTableRows(section, current as ClientNav);
      const editedContacts = this.readTableRows(
        section,
        this.editData as ClientNav,
      );
      const sourceContacts = Array.isArray(originalContacts)
        ? originalContacts
        : [];
      const modifiedContacts = Array.isArray(editedContacts)
        ? editedContacts
        : [];
      if (sourceContacts.length !== modifiedContacts.length) return true;
      // shallow field comparison for each contact record
      for (let i = 0; i < sourceContacts.length; i++) {
        const o: any = sourceContacts[i];
        const e: any = modifiedContacts[i];
        const keys = new Set([
          ...Object.keys(o || {}),
          ...Object.keys(e || {}),
        ]);
        for (const k of keys) {
          if ((o || {})[k] !== (e || {})[k]) return true;
        }
      }
      return false;
    }
    if (Array.isArray(section.fields)) {
      for (const f of section.fields) {
        if (!f.editable) continue;
        const key = f.key;
        const editedVal = this.getValueCaseInsensitive(
          this.editData as any,
          key,
        );
        const currentVal = this.getValueCaseInsensitive(current as any, key);
        if (editedVal !== currentVal) return true;
      }
    }
    return false;
  }

  exportClientData() {
    if (this.isExporting || !this.hasClientData()) return;
    this.isExporting = true;

    this.clientDashboardService.getPresignedUrl(this.clientId).subscribe({
      next: (response) => {
        if (response && response.downloadURL) {
          window.open(response.downloadURL, '_blank');
        }
        this.isExporting = false;
      },
      error: (err) => {
        console.error('Failed to get client export URL', err);
        this.isExporting = false;
      },
      complete: () => {
        this.isExporting = false;
      },
    });
  }
}
